import React, { useState, useEffect, useRef } from 'react';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, LogOut, ChevronDown, DollarSign, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function UserMenu({ onShowSubscription }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [adSpendStatus, setAdSpendStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Fetch user info and ad spend status from backend
  useEffect(() => {
    fetchUserInfo();
    fetchAdSpendStatus();
    
    // Listen for subscription updates (fired after checkout success)
    const handleSubscriptionUpdate = () => {
      console.log('🔄 [UserMenu] Subscription updated, refreshing user info...');
      fetchUserInfo();
      fetchAdSpendStatus();
    };
    
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const fetchUserInfo = async () => {
    console.log('🔍 [UserMenu] Fetching user info...');
    try {
      const token = await getToken();
      console.log('✅ [UserMenu] Token received:', token ? `${token.substring(0, 20)}...` : 'null');
      
      const response = await fetch(`${API_URL}/api/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('📡 [UserMenu] Response status:', response.status);
      
      const data = await response.json();
      console.log('📦 [UserMenu] Response data:', data);
      
      if (data.success) {
        setUserInfo(data);
        console.log('✅ [UserMenu] User info loaded successfully');
      } else {
        console.error('❌ [UserMenu] Failed to fetch user info:', data.error);
      }
    } catch (error) {
      console.error('❌ [UserMenu] Error fetching user info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdSpendStatus = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/user/ad-spend-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setAdSpendStatus(data);
        console.log('💰 [UserMenu] Ad spend status:', data);
      }
    } catch (error) {
      console.error('❌ [UserMenu] Error fetching ad spend status:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleLogout = () => {
    signOut();
  };

  const getTierColor = (tier) => {
    const colors = {
      trial: '#fbbf24',
      starter: '#3b82f6',
      growth: '#8b5cf6',
      scale: '#10b981'
    };
    return colors[tier] || '#6b7280';
  };

  const getTierIcon = (tier) => {
    if (tier === 'trial') return '🎯';
    if (tier === 'starter') return '🚀';
    if (tier === 'growth') return '📈';
    if (tier === 'scale') return '⚡';
    return '👤';
  };

  const formatUsagePercentage = (used, max) => {
    if (!max || max === 0) return 0;
    return Math.min(Math.round((used / max) * 100), 100);
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* User Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: theme.colors.bg.hover,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12,
          padding: '8px 12px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          color: theme.colors.text.primary
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme.mode === 'light' 
            ? 'rgba(99, 102, 241, 0.18)'
            : 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = theme.colors.bg.hover;
        }}
      >
        {/* User Avatar */}
        <div style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: user?.imageUrl ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 600,
          color: 'white',
          overflow: 'hidden'
        }}>
          {user?.imageUrl ? (
            <img src={user.imageUrl} alt={user.fullName || 'User'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <User size={18} color="white" />
          )}
        </div>

        {/* User Info */}
        <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>
            {user?.fullName || user?.firstName || 'User'}
          </span>
          {userInfo && (
            <span style={{ fontSize: 11, color: theme.colors.text.tertiary }}>
              {userInfo.subscription.tierName || 'Free'}
            </span>
          )}
        </div>

        <ChevronDown 
          size={16} 
          color={theme.colors.text.tertiary}
          style={{ 
            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }} 
        />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 8,
          width: 320,
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 16,
          boxShadow: theme.mode === 'light'
            ? '0 8px 32px rgba(0, 0, 0, 0.12)'
            : '0 8px 32px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          zIndex: 1000
        }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: theme.colors.text.tertiary }}>
              Loading...
            </div>
          ) : userInfo ? (
            <>
              {/* User Header */}
              <div style={{ padding: 16, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: user?.imageUrl ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 600,
                    color: 'white',
                    overflow: 'hidden'
                  }}>
                    {user?.imageUrl ? (
                      <img src={user.imageUrl} alt={user.fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      user?.fullName?.charAt(0) || 'U'
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary }}>
                      {user?.fullName || 'User'}
                    </div>
                    <div style={{ fontSize: 13, color: theme.colors.text.tertiary, marginTop: 2 }}>
                      {userInfo.user.email}
                    </div>
                  </div>
                </div>

                {/* Tier Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 8,
                  background: `${getTierColor(userInfo.subscription.tier)}20`,
                  border: `1px solid ${getTierColor(userInfo.subscription.tier)}40`
                }}>
                  <span style={{ fontSize: 14 }}>{getTierIcon(userInfo.subscription.tier)}</span>
                  <span style={{ 
                    fontSize: 13, 
                    fontWeight: 600,
                    color: getTierColor(userInfo.subscription.tier)
                  }}>
                    {userInfo.subscription.tierName}
                  </span>
                </div>
              </div>

              {/* Ad Spend Status */}
              <div style={{ 
                padding: 16, 
                borderBottom: `1px solid ${theme.colors.border.primary}`,
                background: adSpendStatus?.isOverLimit ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.tertiary, marginBottom: 12 }}>
                  AD SPEND THIS MONTH
                </div>
                
                {adSpendStatus ? (
                  <>
                    {/* Ad Spend Progress */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <DollarSign size={14} color={adSpendStatus.isOverLimit ? '#ef4444' : '#f59e0b'} />
                          <span style={{ fontSize: 13, color: theme.colors.text.primary }}>Ad Spend</span>
                        </div>
                        <span style={{ 
                          fontSize: 13, 
                          color: adSpendStatus.isOverLimit ? '#ef4444' : theme.colors.text.secondary,
                          fontWeight: adSpendStatus.isOverLimit ? 600 : 400
                        }}>
                          {formatCurrency(adSpendStatus.currentSpend)} / {adSpendStatus.tierLimit ? formatCurrency(adSpendStatus.tierLimit) : '∞'}
                        </span>
                      </div>
                      <div style={{
                        height: 6,
                        background: theme.colors.bg.tertiary,
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(adSpendStatus.percentage || 0, 100)}%`,
                          background: adSpendStatus.isOverLimit 
                            ? 'linear-gradient(90deg, #ef4444, #f87171)' 
                            : adSpendStatus.percentage > 80 
                              ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                              : 'linear-gradient(90deg, #10b981, #34d399)',
                          transition: 'width 0.3s'
                        }}></div>
                      </div>
                      <div style={{ 
                        fontSize: 11, 
                        color: theme.colors.text.tertiary, 
                        marginTop: 4,
                        textAlign: 'right'
                      }}>
                        {Math.round(adSpendStatus.percentage || 0)}% of {userInfo?.subscription?.tierName || 'plan'} limit
                      </div>
                    </div>

                    {/* Over Limit Warning */}
                    {adSpendStatus.isOverLimit && (
                      <div style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 8
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 8 
                        }}>
                          <AlertTriangle size={16} color="#ef4444" />
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#fca5a5' }}>
                            Ad Spend Limit Exceeded
                          </span>
                        </div>
                        <p style={{ 
                          fontSize: 12, 
                          color: theme.colors.text.secondary, 
                          margin: 0,
                          marginBottom: 10,
                          lineHeight: 1.4
                        }}>
                          Your ad spend exceeds your {userInfo?.subscription?.tierName || 'current'} plan limit. 
                          Upgrade to continue using all features.
                        </p>
                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            if (onShowSubscription) {
                              onShowSubscription();
                            } else {
                              navigate('/subscription');
                            }
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            padding: '10px 16px',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            border: 'none',
                            borderRadius: 8,
                            color: 'white',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <ArrowUpCircle size={16} />
                          Upgrade to {adSpendStatus.nextTier?.name || 'Higher Plan'}
                        </button>
                      </div>
                    )}

                    {/* Approaching Limit Warning (80-100%) */}
                    {!adSpendStatus.isOverLimit && adSpendStatus.percentage >= 80 && (
                      <div style={{
                        background: 'rgba(245, 158, 11, 0.15)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 8,
                        padding: 10
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 6,
                          fontSize: 12,
                          color: '#fbbf24'
                        }}>
                          <AlertTriangle size={14} />
                          <span>Approaching limit - consider upgrading</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 6,
                    fontSize: 13,
                    color: theme.colors.text.tertiary
                  }}>
                    <DollarSign size={14} />
                    <span>Loading ad spend...</span>
                  </div>
                )}
              </div>

              {/* Menu Items */}
              <div style={{ padding: 8 }}>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/settings');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    color: theme.colors.text.primary,
                    fontSize: 14,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.hover}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Settings size={18} color={theme.colors.text.tertiary} />
                  <span>Account Settings</span>
                </button>

                <button
                  onClick={() => {
                    setShowDropdown(false);
                    if (onShowSubscription) {
                      onShowSubscription();
                    }
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    color: theme.colors.text.primary,
                    fontSize: 14,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.hover}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <CreditCard size={18} color={theme.colors.text.tertiary} />
                  <span>Billing & Plans</span>
                </button>

                <div style={{ height: 1, background: theme.colors.border.primary, margin: '8px 0' }}></div>

                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: 14,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: theme.colors.text.tertiary }}>
              Failed to load user info
            </div>
          )}
        </div>
      )}
    </div>
  );
}

